import { STORES, STORES_KEY } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';

/** Minimal store shape returned by the Commercetools backend. */
export interface Store {
  id: string;
  key: string;
  version?: number;
  name?: Record<string, string> | string;
  languages?: string[];
  distributionChannels?: { id: string; typeId: string }[];
  supplyChannels?: { id: string; typeId: string }[];
  [key: string]: unknown;
}

/** Response envelope for the stores list endpoint. */
interface StoresListResponse {
  results?: Store[];
}

export const storeService = {
  /**
   * Fetch store by ID via backend API.
   * Note: Backend only supports fetch by key, so we fetch all stores and filter by ID.
   * If the store key is available, prefer using fetchStoreByKey instead.
   * @param storeId - The ID of the store to fetch
   */
  async fetchStore(storeId: string): Promise<Store | null> {
    if (!storeId) {
      return null;
    }

    try {
      const res = await api.get(STORES);

      if (!res.ok) throw new Error(`Failed to fetch stores: ${res.status}`);

      const data: StoresListResponse = await res.json();

      const store = data.results?.find((s) => s.id === storeId);
      if (!store) {
        return null;
      }
      return store;
    } catch (error) {
      console.error('[storeService] Failed to fetch store by ID:', error);
      return null;
    }
  },

  /**
   * Fetch store by key via backend API.
   * @param storeKey - The key of the store to fetch
   */
  async fetchStoreByKey(storeKey: string): Promise<Store | null> {
    if (!storeKey) {
      return null;
    }

    try {
      const res = await api.get(STORES_KEY(storeKey));

      if (!res.ok) throw new Error(`Failed to fetch store: ${res.status}`);
      return (await res.json()) as Store;
    } catch (error) {
      console.error('[storeService] Failed to fetch store by key:', error);
      return null;
    }
  },
};
